import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Shepherd from 'shepherd.js'
import { applyProductTourShepherdSteps } from '@/components/product-tour/productTourShepherdSteps'
import {
	cancelIfActive,
	type ShepherdTour,
} from '@/components/product-tour/utils/shepherdTourCancel'
import { useAuth } from '@/context/AuthContext'
import {
	clearProductTourSessionPending,
	PRODUCT_TOUR_STEP_COUNT,
	shouldAutoStartProductTourOnProjects,
	useProductTour,
} from '@/context/ProductTourContext'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR_STEP } from '@/lib/product-tour/stepIndices'

type ActiveTourState = {
	tour: ShepherdTour
	onCancel: () => void
}

function teardownShepherdTour(state: ActiveTourState) {
	state.tour.off('cancel', state.onCancel)
	cancelIfActive(state.tour)
}

function ProductTourAutoStart() {
	const { user } = useAuth()
	const ctx = useProductTour()
	const location = useLocation()
	const startedRef = useRef(false)

	useEffect(() => {
		if (location.pathname !== '/projects') {
			startedRef.current = false
			return
		}
		if (!user?.id || ctx.isTourActive) return
		if (!shouldAutoStartProductTourOnProjects(user.id, user.product_tour_state)) return
		if (startedRef.current) return
		startedRef.current = true
		clearProductTourSessionPending()
		ctx.startTour()
	}, [ctx, location.pathname, user?.id, user?.product_tour_state])

	return null
}

function ProductTourRouteSync() {
	const ctx = useProductTour()
	const location = useLocation()
	const navigate = useNavigate()

	useEffect(() => {
		if (!ctx.isTourActive) return

		const { stepIndex } = ctx

		if (stepIndex === TOUR_STEP.workspaceSettings && location.pathname !== '/settings') {
			navigate('/settings')
			return
		}

		if (stepIndex === TOUR_STEP.newProject && location.pathname === '/settings') {
			navigate('/projects')
		}
	}, [ctx.isTourActive, ctx.stepIndex, location.pathname, navigate])

	return null
}

function ProductTourRuntime() {
	const ctx = useProductTour()
	const { t } = useTranslation()
	const tourStateRef = useRef<ActiveTourState | null>(null)
	const ctxRef = useRef(ctx)
	const mountedStepRef = useRef<string | null>(null)
	const effectTokenRef = useRef(0)
	ctxRef.current = ctx

	useEffect(() => {
		effectTokenRef.current += 1
		const effectToken = effectTokenRef.current

		if (!ctx.isTourActive) {
			const existing = tourStateRef.current
			if (existing) {
				teardownShepherdTour(existing)
				tourStateRef.current = null
			}
			return
		}

		const idx = ctx.stepIndex
		const stepId = `s${idx}`
		const existing = tourStateRef.current
		if (existing?.tour.isActive() && mountedStepRef.current === stepId) {
			return
		}
		if (existing) {
			teardownShepherdTour(existing)
			tourStateRef.current = null
		}

		const tour = new Shepherd.Tour({
			useModalOverlay: true,
			exitOnEsc: true,
			keyboardNavigation: false,
			defaultStepOptions: {
				cancelIcon: { enabled: true },
				scrollTo: { behavior: 'smooth', block: 'center' },
				classes: 'mm-shepherd-step',
				floatingUIOptions: {
					strategy: 'fixed',
				},
			},
		})

		const onCancel = () => {
			ctxRef.current.exitTour('skipped')
		}

		tour.on('cancel', onCancel)

		tour.on('complete', () => {
			const c = ctxRef.current
			if (c.stepIndex !== idx) return
			if (c.stepIndex < PRODUCT_TOUR_STEP_COUNT - 1) {
				c.nextStep()
			} else {
				c.exitTour('completed')
			}
		})

		tourStateRef.current = { tour, onCancel }

		applyProductTourShepherdSteps({
			tour,
			stepIndex: idx,
			text: (key) => t(key) as string,
		})

		const startTimer = window.setTimeout(() => {
			if (effectToken !== effectTokenRef.current) return
			mountedStepRef.current = stepId
			tour.start()
		}, 150)

		return () => {
			window.clearTimeout(startTimer)
			if (tourStateRef.current?.tour === tour) {
				teardownShepherdTour(tourStateRef.current)
				tourStateRef.current = null
			}
			if (mountedStepRef.current === stepId) {
				mountedStepRef.current = null
			}
		}
	}, [ctx.isTourActive, ctx.stepIndex])

	return null
}

/** Mount inside Router + ProductTourProvider */
export function ProductTourShepherd() {
	return (
		<>
			<ProductTourAutoStart />
			<ProductTourRouteSync />
			<ProductTourRuntime />
		</>
	)
}
